import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Share, FilePdf, ArrowClockwise, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import ReportContent from "@/components/ReportContent";

export default function ReportView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [error, setError] = useState(false);
  const [rerunning, setRerunning] = useState(false);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const res = await api.get(`/reports/${id}`);
      setReport(res.data);
      if (res.data.status === "running" || res.data.status === "pending") {
        if (!pollRef.current) pollRef.current = setInterval(load, 3000);
      } else {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setRerunning(false);
      }
    } catch {
      setError(true);
    }
  };

  useEffect(() => {
    load();
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line
  }, [id]);

  const share = () => {
    const url = `${window.location.origin}/r/${id}`;
    navigator.clipboard.writeText(url);
    toast.success("Paylaşım linki kopyalandı");
  };

  const rerun = async () => {
    setRerunning(true);
    try {
      await api.post(`/reports/${id}/rerun`);
      toast.info("Rapor yeniden çalıştırılıyor…");
      load();
    } catch {
      setRerunning(false);
      toast.error("Yeniden çalıştırılamadı.");
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader />
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <h1 className="font-heading text-2xl font-semibold">Rapor bulunamadı</h1>
          <button onClick={() => navigate("/dashboard")} className="mt-4 text-brand font-medium">← Panele dön</button>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader />
        <div className="font-mono text-sm text-muted-foreground py-24 text-center animate-pulse">Yükleniyor…</div>
      </div>
    );
  }

  const running = report.status === "running" || report.status === "pending";

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="no-print flex items-center justify-between mb-8 gap-4">
          <button onClick={() => navigate("/dashboard")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="back-btn">
            <ArrowLeft size={16} /> Panel
          </button>
          {report.status === "done" && (
            <div className="flex items-center gap-2">
              <button onClick={rerun} disabled={rerunning} data-testid="rerun-btn"
                className="flex items-center gap-1.5 border border-input px-4 py-2.5 text-sm font-medium hover:border-brand transition-colors disabled:opacity-60">
                <ArrowClockwise size={16} className={rerunning ? "animate-spin" : ""} /> Yeniden çalıştır
              </button>
              <button onClick={share} data-testid="share-btn"
                className="flex items-center gap-1.5 border border-input px-4 py-2.5 text-sm font-medium hover:border-brand transition-colors">
                <Share size={16} /> Paylaş
              </button>
              <button onClick={() => window.print()} data-testid="pdf-btn"
                className="flex items-center gap-1.5 bg-brand text-white px-4 py-2.5 text-sm font-medium transition-transform hover:-translate-y-0.5">
                <FilePdf size={16} /> PDF indir
              </button>
            </div>
          )}
        </div>

        {running ? (
          <div className="border border-border p-12 text-center" data-testid="report-running">
            <div className="font-mono text-sm text-brand mb-4">{report.progress_msg || "Çalışıyor…"}</div>
            <div className="h-2 bg-muted max-w-md mx-auto">
              <div className="h-full bg-brand transition-all duration-700" style={{ width: `${report.progress || 0}%` }} />
            </div>
            <p className="text-muted-foreground text-sm mt-6">Rapor hazırlanıyor, bu sayfa otomatik güncellenir.</p>
          </div>
        ) : report.status === "failed" ? (
          <div className="border border-border p-12 text-center" data-testid="report-failed">
            <h2 className="font-heading text-xl font-semibold">Rapor oluşturulamadı</h2>
            <p className="text-muted-foreground text-sm mt-2">{report.progress_msg}</p>
            <button onClick={rerun} className="mt-6 inline-flex items-center gap-2 bg-brand text-white px-5 py-2.5 font-medium">
              <ArrowClockwise size={16} /> Tekrar dene
            </button>
          </div>
        ) : (
          <div data-testid="report-content">
            <ReportContent report={report} />
          </div>
        )}
      </main>
    </div>
  );
}
